import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../components/Navbar';
import { Button } from '../components/ui/button';
import { Check, Sparkles, Loader2, ArrowRight } from 'lucide-react';
import { listPackages, createCheckout } from '../paymentsApi';
import { toast } from '../hooks/use-toast';
import { Toaster } from '../components/ui/toaster';

const PlanFeatures = {
  starter_monthly: [
    '1 AI worker included',
    '2,000 credits / month',
    'All integrations',
    'Email support',
  ],
  pro_monthly: [
    'Up to 5 AI workers',
    '10,000 credits / month',
    'Priority support',
    'Team workspace',
    'Advanced analytics',
  ],
  enterprise_monthly: [
    'Unlimited AI workers',
    '50,000 credits / month',
    'SSO + SAML',
    'Dedicated success manager',
    'SLA & audit logs',
    'Custom integrations',
  ],
  credits_1k: ['1,000 credits', 'Never expire', 'One-time payment'],
  credits_5k: ['5,000 credits', 'Save 15%', 'One-time payment'],
  credits_10k: ['10,000 credits', 'Save 25%', 'One-time payment'],
};

const formatPrice = (amount, currency) => {
  const symbols = { usd: '$', eur: '€', gbp: '£' };
  const symbol = symbols[currency?.toLowerCase()] || `${currency?.toUpperCase()} `;
  return `${symbol}${amount.toFixed(0)}`;
};

const Pricing = () => {
  const navigate = useNavigate();
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [checkoutId, setCheckoutId] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const pkgs = await listPackages();
        setPackages(pkgs);
      } catch {
        toast({ title: 'Failed to load plans', description: 'Please refresh the page.' });
      } finally {
        setLoading(false);
      }
    })();

    // Show canceled toast if user came back from Stripe cancel
    const params = new URLSearchParams(window.location.search);
    if (params.get('canceled')) {
      toast({ title: 'Checkout canceled', description: 'No charge was made. You can try again anytime.' });
    }
  }, []);

  const handleCheckout = async (pkgId) => {
    setCheckoutId(pkgId);
    try {
      const { url } = await createCheckout(pkgId);
      if (url) window.location.href = url;
    } catch (e) {
      toast({ title: 'Checkout failed', description: e?.response?.data?.detail || 'Please try again.' });
      setCheckoutId(null);
    }
  };

  const subs = packages.filter((p) => p.type === 'subscription');
  const credits = packages.filter((p) => p.type === 'credits');

  return (
    <div className="min-h-screen bg-white text-neutral-900">
      <Navbar onSignIn={() => navigate('/')} onSignUp={() => navigate('/')} />

      <section className="mx-auto max-w-5xl px-6 pt-16 text-center">
        <div className="mb-5 inline-flex items-center gap-2 rounded-full border border-neutral-200 bg-white px-3 py-1 text-xs text-neutral-600 shadow-sm">
          <Sparkles className="h-3.5 w-3.5 text-neutral-500" />
          <span>Simple, transparent pricing</span>
        </div>
        <h1 className="font-serif text-5xl leading-[1.05] tracking-tight text-neutral-900 sm:text-6xl">
          Hire AI workers <span className="italic text-neutral-500">on your terms</span>.
        </h1>
        <p className="mx-auto mt-4 max-w-xl text-neutral-600 sm:text-lg">
          Pick a monthly plan or top-up with credits. Cancel anytime.
        </p>
      </section>

      {loading ? (
        <div className="flex items-center justify-center py-24 text-neutral-500">
          <Loader2 className="h-6 w-6 animate-spin" />
        </div>
      ) : (
        <>
          {/* Subscriptions */}
          <section className="mx-auto max-w-6xl px-6 py-12">
            <div className="grid grid-cols-1 gap-5 md:grid-cols-3">
              {subs.map((pkg, idx) => {
                const highlighted = idx === 1;
                return (
                  <div
                    key={pkg.id}
                    className={
                      'relative flex flex-col rounded-2xl border p-6 transition-all duration-200 ' +
                      (highlighted
                        ? 'border-neutral-900 bg-neutral-900 text-white shadow-xl'
                        : 'border-neutral-200 bg-white hover:border-neutral-300 hover:shadow-md')
                    }
                  >
                    {highlighted && (
                      <div className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full bg-white px-2.5 py-0.5 text-[11px] font-semibold uppercase tracking-wider text-neutral-900">
                        Most popular
                      </div>
                    )}
                    <h3 className={'text-lg font-semibold ' + (highlighted ? 'text-white' : 'text-neutral-900')}>
                      {pkg.name}
                    </h3>
                    <p className={'mt-1 text-sm ' + (highlighted ? 'text-neutral-300' : 'text-neutral-500')}>
                      {pkg.description}
                    </p>

                    <div className="mt-5 flex items-baseline gap-1">
                      <span className="font-serif text-5xl tracking-tight">{formatPrice(pkg.amount, pkg.currency)}</span>
                      <span className={'text-sm ' + (highlighted ? 'text-neutral-300' : 'text-neutral-500')}>/month</span>
                    </div>

                    <ul className="mt-6 space-y-2">
                      {(PlanFeatures[pkg.id] || []).map((f) => (
                        <li key={f} className="flex items-start gap-2 text-[14px]">
                          <Check
                            className={'mt-0.5 h-4 w-4 shrink-0 ' + (highlighted ? 'text-emerald-400' : 'text-emerald-600')}
                            strokeWidth={2.5}
                          />
                          <span>{f}</span>
                        </li>
                      ))}
                    </ul>

                    <Button
                      onClick={() => handleCheckout(pkg.id)}
                      disabled={checkoutId === pkg.id}
                      className={
                        'mt-8 w-full ' +
                        (highlighted
                          ? 'bg-white text-neutral-900 hover:bg-neutral-100'
                          : 'bg-neutral-900 text-white hover:bg-neutral-800')
                      }
                    >
                      {checkoutId === pkg.id ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <>
                          Subscribe <ArrowRight className="ml-1.5 h-4 w-4" />
                        </>
                      )}
                    </Button>
                  </div>
                );
              })}
            </div>
          </section>

          {/* Credit Packs */}
          <section className="mx-auto max-w-6xl px-6 pb-20">
            <div className="mb-8 text-center">
              <h2 className="font-serif text-3xl tracking-tight text-neutral-900 sm:text-4xl">Or top-up with credits</h2>
              <p className="mt-2 text-neutral-600">One-time purchase, credits never expire.</p>
            </div>
            <div className="grid grid-cols-1 gap-5 md:grid-cols-3">
              {credits.map((pkg) => (
                <div
                  key={pkg.id}
                  className="flex flex-col rounded-2xl border border-neutral-200 bg-white p-6 transition-all duration-200 hover:border-neutral-300 hover:shadow-md"
                >
                  <h3 className="text-lg font-semibold text-neutral-900">{pkg.name}</h3>
                  <p className="mt-1 text-sm text-neutral-500">{pkg.description}</p>
                  <div className="mt-5 flex items-baseline gap-1">
                    <span className="font-serif text-4xl tracking-tight">{formatPrice(pkg.amount, pkg.currency)}</span>
                    <span className="text-sm text-neutral-500">one-time</span>
                  </div>
                  <ul className="mt-5 space-y-2">
                    {(PlanFeatures[pkg.id] || []).map((f) => (
                      <li key={f} className="flex items-start gap-2 text-[14px] text-neutral-700">
                        <Check className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" strokeWidth={2.5} />
                        <span>{f}</span>
                      </li>
                    ))}
                  </ul>
                  <Button
                    onClick={() => handleCheckout(pkg.id)}
                    disabled={checkoutId === pkg.id}
                    variant="outline"
                    className="mt-6 w-full border-neutral-300 bg-white text-neutral-900 hover:bg-neutral-100"
                  >
                    {checkoutId === pkg.id ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Buy credits'}
                  </Button>
                </div>
              ))}
            </div>
          </section>
        </>
      )}

      <Toaster />
    </div>
  );
};

export default Pricing;
